import { AxiosResponse } from 'axios'
import axios from '@/services/axios'
import type { ArenaProductInterface } from '@/models/shop/ArenaProduct.types'

const SHOP_ROUTE = process.env.VUE_APP_SHOP_V2_BASE_URI

export default abstract class ArenaProductsApi {
  public static baseRoute = `${SHOP_ROUTE}/arena-catalog/arenas`
  public static entity = 'products'

  public static async list(
    arenaId: string,
    params: Record<string, unknown> = {},
  ): Promise<
    AxiosResponse<{
      data: ArenaProductInterface[]
      metadata: { count: number; page: number; size: number; sort: string }
    }>
  > {
    return await axios.get(`${this.baseRoute}/${arenaId}/${this.entity}`, {
      params,
    })
  }

  public static async create(
    arenaId: string,
    data: object,
  ): Promise<AxiosResponse> {
    return await axios.post(`${this.baseRoute}/${arenaId}/${this.entity}`, data)
  }

  public static async get(arenaId: string, id: string): Promise<AxiosResponse> {
    return await axios.get(`${this.baseRoute}/${arenaId}/${this.entity}/${id}`)
  }

  public static async update(
    arenaId: string,
    id: string,
    data: object,
  ): Promise<AxiosResponse> {
    return await axios.patch(
      `${this.baseRoute}/${arenaId}/${this.entity}/${id}`,
      data,
    )
  }

  public static async delete(
    arenaId: string,
    id: string,
  ): Promise<AxiosResponse> {
    return await axios.delete(
      `${this.baseRoute}/${arenaId}/${this.entity}/${id}`,
    )
  }

  public static async addProductTax(
    arenaId: string,
    id: string,
    data: object,
  ): Promise<AxiosResponse> {
    const url = `${this.baseRoute}/${arenaId}/${this.entity}/${id}/add-tax`
    return await axios.post(url, data)
  }

  public static async removeProductTax(
    arenaId: string,
    id: string,
    data: object,
  ): Promise<AxiosResponse> {
    const url = `${this.baseRoute}/${arenaId}/${this.entity}/${id}/remove-tax`
    return await axios.post(url, data)
  }

  public static async createArenaVariant(
    arenaId: string,
    id: string,
    data: object,
  ): Promise<AxiosResponse> {
    return await axios.post(
      `${this.baseRoute}/${arenaId}/${this.entity}/${id}/variants`,
      data,
    )
  }

  public static async getArenaVariant(
    arenaId: string,
    id: string,
    variantId: string,
  ): Promise<AxiosResponse> {
    return await axios.get(
      `${this.baseRoute}/${arenaId}/${this.entity}/${id}/variants/${variantId}`,
    )
  }

  public static async updateArenaVariant(
    arenaId: string,
    id: string,
    variantId: string,
    data: object,
  ): Promise<AxiosResponse> {
    return await axios.patch(
      `${this.baseRoute}/${arenaId}/${this.entity}/${id}/variants/${variantId}`,
      data,
    )
  }

  public static async deleteArenaVariant(
    arenaId: string,
    id: string,
    variantId: string,
  ): Promise<AxiosResponse> {
    return await axios.delete(
      `${this.baseRoute}/${arenaId}/${this.entity}/${id}/variants/${variantId}`,
    )
  }

  public static async createArenaVariantPrice(
    arenaId: string,
    id: string,
    variantId: string,
    data: object,
  ): Promise<AxiosResponse> {
    return await axios.post(
      `${this.baseRoute}/${arenaId}/${this.entity}/${id}/variants/${variantId}/prices`,
      data,
    )
  }

  public static async updateArenaVariantPrice(
    arenaId: string,
    id: string,
    variantId: string,
    variantPriceId: string,
    data: object,
  ): Promise<AxiosResponse> {
    return await axios.patch(
      `${this.baseRoute}/${arenaId}/${this.entity}/${id}/variants/${variantId}/prices/${variantPriceId}`,
      data,
    )
  }

  public static async deleteArenaVariantPrice(
    arenaId: string,
    id: string,
    variantId: string,
    variantPriceId: string,
  ): Promise<AxiosResponse> {
    return await axios.delete(
      `${this.baseRoute}/${arenaId}/${this.entity}/${id}/variants/${variantId}/prices/${variantPriceId}`,
    )
  }

  public static async getArenaVariants(
    arenaId: string,
    params?: Record<'name' | 'currency_id', string>,
  ): Promise<AxiosResponse> {
    return await axios.get(`${this.baseRoute}/${arenaId}/variants`, {
      params,
    })
  }
}
